import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { t } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Sparkles, Copy, CheckCircle2, Loader2, ArrowRight, Leaf, MessageCircle, NotebookPen,
} from "lucide-react";

export default function OnboardingTour() {
  const { user, refreshUser } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0=welcome, 1=coach-intro, 2=journal-write, 3=journal-suggestion
  const [form, setForm] = useState({ context: "family", observation: "", feeling: "", need: "", request: "" });
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.onboarded === false) setOpen(true);
  }, [user?.id, user?.onboarded]);

  const loadExample = async () => {
    const { data } = await api.get(`/content/onboarding-example?language=${lang}`);
    setForm({ ...form, ...data });
  };

  const refine = async () => {
    if (!form.observation || !form.feeling || !form.need || !form.request) {
      toast.error(t(lang, "error"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/rewrite-journal", { ...form, notes: "", language: lang });
      setSuggestion(data);
      setStep(3);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.upgrade) toast.error(t(lang, "limit_reached_rewrite"));
      else toast.error(t(lang, "error"));
    } finally {
      setLoading(false);
    }
  };

  const copyMessage = async () => {
    if (!suggestion?.message) return;
    try {
      await navigator.clipboard.writeText(suggestion.message);
      toast.success(t(lang, "onboard_copied"));
    } catch (e) {
      console.warn("clipboard write failed", e?.message || e);
      toast.error(t(lang, "error"));
    }
  };

  const finish = async () => {
    try {
      await api.post("/user/onboarded");
      await refreshUser?.();
    } catch (e) {
      console.warn("mark onboarded failed", e?.message || e);
    }
    setOpen(false);
  };

  const goToCoach = async () => {
    try { await api.post("/user/onboarded"); } catch (e) { console.warn("mark onboarded failed", e?.message || e); }
    await refreshUser?.();
    setOpen(false);
    navigate("/app/practice");
  };

  const totalSteps = 4;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="max-w-2xl" data-testid="onboarding-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center text-3xl shrink-0 select-none">
              🐒
            </div>
            <div>
              <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                {step === 0 && t(lang, "onboard_welcome_title")}
                {step === 1 && t(lang, "onboard_coach_title")}
                {step === 2 && t(lang, "onboard_step1")}
                {step === 3 && t(lang, "ai_suggestion")}
              </DialogTitle>
              <DialogDescription>
                {step === 0 && t(lang, "onboard_welcome_body")}
                {step === 1 && t(lang, "onboard_coach_body")}
                {step === 2 && t(lang, "onboard_write_below")}
                {step === 3 && t(lang, "onboard_step3")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Progress value={((step + 1) / totalSteps) * 100} className="h-1" />

        {/* Step 0: Welcome overview */}
        {step === 0 && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-accent/10 border border-accent/30">
                <div className="w-9 h-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading font-medium">{t(lang, "onboard_bullet_coach")}</div>
                  <p className="text-sm text-muted-foreground mt-1">{t(lang, "onboard_bullet_coach_desc")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-secondary/60">
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <NotebookPen className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading font-medium">{t(lang, "onboard_bullet_journal")}</div>
                  <p className="text-sm text-muted-foreground mt-1">{t(lang, "onboard_bullet_journal_desc")}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={finish} data-testid="onboard-skip">{t(lang, "skip")}</Button>
              <Button onClick={() => setStep(1)} className="rounded-full gap-2" data-testid="onboard-start">
                {t(lang, "onboard_next")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 1: Coach spotlight */}
        {step === 1 && (
          <div className="space-y-5 py-2">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-accent/20 to-secondary border border-accent/40 relative overflow-hidden">
              <MessageCircle className="w-10 h-10 text-accent mb-3" strokeWidth={1.5} />
              <p className="font-heading text-xl leading-relaxed">
                {t(lang, "onboard_coach_pitch")}
              </p>
              <div className="mt-4 space-y-2">
                {["onboard_coach_bullet1", "onboard_coach_bullet2", "onboard_coach_bullet3"].map((k) => (
                  <div key={k} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span>{t(lang, k)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep(2)} data-testid="onboard-coach-later">
                {t(lang, "onboard_coach_later")}
              </Button>
              <Button onClick={goToCoach} className="rounded-full gap-2" data-testid="onboard-coach-try">
                <MessageCircle className="w-4 h-4" /> {t(lang, "onboard_coach_try")}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Journal form */}
        {step === 2 && (
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <Button variant="outline" size="sm" onClick={loadExample} className="rounded-full gap-2" data-testid="onboard-load-example">
              <Sparkles className="w-4 h-4" /> {t(lang, "onboard_use_example")}
            </Button>
            {["observation", "feeling", "need", "request"].map((k) => (
              <div key={k} className="space-y-1.5">
                <Label className="font-heading">{t(lang, k)}</Label>
                <Textarea
                  data-testid={`onboard-${k}`}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="p-3 min-h-[60px]"
                />
              </div>
            ))}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={finish} data-testid="onboard-skip-2">{t(lang, "skip")}</Button>
              <Button onClick={refine} disabled={loading} className="rounded-full gap-2" data-testid="onboard-refine">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {t(lang, "onboard_refine_now")}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: AI Suggestion */}
        {step === 3 && suggestion && (
          <div className="space-y-4 py-2">
            <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
              <div className="text-xs uppercase tracking-widest text-accent font-medium mb-2">Message</div>
              <p className="leading-relaxed" data-testid="onboard-message">{suggestion.message}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={copyMessage} variant="outline" className="rounded-full gap-2 flex-1" data-testid="onboard-copy-btn">
                <Copy className="w-4 h-4" /> {t(lang, "onboard_copy")}
              </Button>
              <Button onClick={finish} className="rounded-full gap-2 flex-1" data-testid="onboard-finish">
                <CheckCircle2 className="w-4 h-4" /> {t(lang, "onboard_finish")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
